import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { CompanyLogo } from "../../components/CompanyLogo";
import { Card, ErrorBox, FreshnessStamp, Skeleton } from "../../components/ui";
import { useAssignments } from "../../lib/api";

const SIDE_PILL: Record<string, string> = {
  Employee: "bg-blue-50 text-brand-primary",
  Contractor: "bg-violet-50 text-violet-700",
};

/** Who is staff, who is a contractor, and since when (WFA-01 … WFA-04, OS-0622).
 *
 * The register exists because nothing in ERPNext can say two records are the same person: `Employee` carries no
 * natural key, and `Contractor` is named after somebody's full name. Without it, a mason hired off an agency crew
 * becomes two unrelated people and their time is counted twice. */
export default function Assignments({ company }: { company?: string }) {
  const { t } = useTranslation();
  const q = useAssignments(company);
  const [search, setSearch] = useState("");

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-4 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const term = search.trim().toLowerCase();
  const people = d.people.filter((p) => !term || p.person_name.toLowerCase().includes(term));

  return (
    <div className="mt-4 space-y-6" data-wfa-page>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-sm text-brand-grey">{t("wfa.hint")}</p>
        <FreshnessStamp lastUpdated={d.as_of} />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-wfa-stats>
        <Stat label={t("wfa.stats.people")} value={d.counts.people} />
        <Stat label={t("wfa.stats.employees")} value={d.counts.employees_today} />
        <Stat label={t("wfa.stats.contractors")} value={d.counts.contractors_today} />
        <Stat label={t("wfa.stats.changedSide")} value={d.counts.changed_side} hint={t("wfa.stats.changedSideHint")} />
      </div>

      {d.unlinked.contractors.length > 0 && (
        <div className="rounded-lg border border-status-amber/40 bg-amber-50 px-4 py-3 text-sm" data-wfa-unlinked>
          <div className="font-semibold text-status-amber">{t("wfa.unlinked.title", { n: d.unlinked.contractors.length })}</div>
          <p className="mt-1 text-[12px] text-brand-charcoal">{t("wfa.unlinked.hint")}</p>
          <p className="mt-1 text-[11px] text-brand-grey">{d.unlinked.contractors.join(", ")}</p>
        </div>
      )}

      {d.changed_side.length > 0 && (
        <Card className="p-4" data-wfa-changed>
          <h2 className="font-heading text-base font-semibold">{t("wfa.changed.title")}</h2>
          <p className="mt-1 text-[11px] text-brand-grey">{t("wfa.changed.hint")}</p>
          <ul className="mt-2 space-y-2 text-sm">
            {d.changed_side.map((p) => (
              <li key={p.name} className="border-t border-brand-grey-light/70 pt-2">
                <Link to={`/people/assignments/${encodeURIComponent(p.name)}`} className="font-semibold text-brand-primary hover:underline">
                  {p.person_name}
                </Link>
                <div className="mt-1 flex flex-wrap gap-1">
                  {p.periods.map((period) => (
                    <span key={period.name} className={`rounded px-2 py-0.5 text-[11px] ${SIDE_PILL[period.assignment_type]}`}>
                      {t(`wfa.side.${period.assignment_type}`)} {period.from_date} → {period.to_date ?? t("wfa.open")}
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="overflow-hidden" data-wfa-list>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-grey-light px-4 py-3">
          <h2 className="font-heading text-base font-semibold">{t("wfa.register")}</h2>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("common.search")} data-wfa-search
            className="w-56 rounded border border-brand-grey-light px-3 py-1.5 text-sm focus:border-brand-primary focus:outline-none" />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
            <tr>
              <th className="px-4 py-2">{t("wfa.person")}</th>
              <th className="px-4 py-2">{t("wfa.today")}</th>
              <th className="px-4 py-2">{t("wfa.since")}</th>
              <th className="px-2 py-2 text-right">{t("wfa.periods")}</th>
              <th className="px-4 py-2">{t("wfa.company")}</th>
            </tr>
          </thead>
          <tbody>
            {people.map((p) => {
              const current = p.periods.find((x) => x.state === "Current");
              return (
                <tr key={p.name} className="border-t border-brand-grey-light hover:bg-[#F6F8FA]" data-wfa-row={p.name}>
                  <td className="px-4 py-2">
                    <Link to={`/people/assignments/${encodeURIComponent(p.name)}`} className="font-semibold text-brand-primary hover:underline">
                      {p.person_name}
                    </Link>
                    {p.both_sides && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-status-amber">{t("wfa.bothSides")}</span>}
                  </td>
                  <td className="px-4 py-2">
                    {p.current_type
                      ? <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${SIDE_PILL[p.current_type]}`}>{t(`wfa.side.${p.current_type}`)}</span>
                      : <span className="text-brand-grey">{t("wfa.unengaged")}</span>}
                  </td>
                  <td className="px-4 py-2 text-brand-grey">{p.current_since ?? "–"}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{p.assignments}</td>
                  <td className="px-4 py-2">{current?.company ? <CompanyLogo company={current.company} size={16} showName /> : "–"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {people.length === 0 && <p className="py-6 text-center text-sm text-brand-grey">{t("common.noRows")}</p>}
      </Card>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <Card className="px-4 py-3">
      <div className="font-heading text-2xl font-semibold text-brand-primary">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-brand-grey">{label}</div>
      {hint && <div className="mt-0.5 text-[11px] text-brand-grey">{hint}</div>}
    </Card>
  );
}
